<template>
  <div class="inv-report">
    <div class="inv-report__head">
      <ButtonPrime icon="pi pi-arrow-left" class="p-button-text p-button-sm mb-2" label="Volver al hub" @click="router.push({ name: 'estudyoo-hub' })" />
      <h1 class="inv-report__title">Inventario Estudyoo</h1>
      <p class="inv-report__lead">
        Conteos según lo sincronizado desde legacy. Elige un colegio para acotar o deja «Todos» para ver el total global.
        Este informe solo se carga al entrar aquí (no en el hub).
      </p>
    </div>

    <DjangoAuthGate class="inv-report__gate" @authenticated="onDjangoAuth" />

    <section v-if="djangoOk" class="inv-report__panel">
      <div class="inv-report__panel-head">
        <div class="inv-report__panel-actions">
          <DropdownPrime
            v-model="selColegioId"
            :options="colegios"
            option-label="label"
            option-value="id"
            placeholder="Todos los colegios"
            class="inv-report__colegio-dd"
            show-clear
            filter
            :loading="cargandoColegios"
            @change="cargarResumen"
          />
          <ButtonPrime
            icon="pi pi-refresh"
            label="Actualizar"
            class="p-button-outlined p-button-sm"
            :loading="cargandoResumen"
            :disabled="cargandoResumen"
            @click="cargarResumen"
          />
        </div>
      </div>

      <div v-if="resumenError" class="inv-report__alert">{{ resumenError }}</div>

      <div v-if="!cargado && !cargandoResumen" class="inv-report__empty">
        <i class="pi pi-chart-bar" />
        <p>Pulsa <strong>Actualizar</strong> para cargar los conteos.</p>
      </div>

      <div v-else class="inv-report__stat-groups">
        <div v-for="(grupo, gi) in statGroups" :key="grupo.id" class="inv-report__stat-group">
          <h3 class="inv-report__stat-group-title">{{ grupo.title }}</h3>
          <div class="inv-report__stats">
            <div
              v-for="(stat, si) in grupo.items"
              :key="stat.key"
              :class="['inv-report__stat', { 'inv-report__stat--visible': statsVisible && cargado }]"
              :style="{ transitionDelay: `${(gi * 4 + si) * 45}ms` }"
            >
              <span class="inv-report__stat-icon"><i :class="`pi ${stat.icon}`" aria-hidden="true" /></span>
              <span class="inv-report__stat-value">
                <template v-if="cargandoResumen">—</template>
                <template v-else>{{ fmtNum(conteos[stat.key]) }}</template>
              </span>
              <span class="inv-report__stat-label">{{ stat.label }}</span>
              <span v-if="stat.hint && cargado && !cargandoResumen && conteos[stat.key]" class="inv-report__stat-hint">{{ stat.hint(conteos) }}</span>
            </div>
          </div>
        </div>
      </div>

      <div v-if="syncInfo.ultima_sesion && cargado && !cargandoResumen" class="inv-report__sync-banner">
        <i class="pi pi-history" />
        <span>
          Último sync: <strong>{{ syncInfo.ultima_sesion.nombre }}</strong>
          <span v-if="syncInfo.ultima_sesion.finalizada_en" class="text-500">
            · {{ fmtFecha(syncInfo.ultima_sesion.finalizada_en) }}
          </span>
        </span>
        <ButtonPrime label="Ir a sync" icon="pi pi-sync" class="p-button-sm p-button-text" @click="router.push({ name: 'sync-django' })" />
      </div>
      <p v-else-if="cargado && !cargandoResumen && selColegioId && !syncInfo.colegio_id_externo" class="inv-report__sync-hint">
        Este colegio aún no tiene espejo legacy. Usa <strong>Sincronizar desde legacy</strong> para importar cursos, capítulos y alumnos.
      </p>

      <div v-if="cargado" class="inv-report__links flex flex-wrap gap-2 mt-3">
        <ButtonPrime label="Reporte por colegio" icon="pi pi-table" class="p-button-outlined p-button-sm" @click="router.push({ name: 'estudyoo-reporte-sync' })" />
        <ButtonPrime label="Mapa capítulos" icon="pi pi-sitemap" class="p-button-outlined p-button-sm" @click="router.push({ name: 'estudyoo-mapa-capitulos' })" />
      </div>
    </section>
  </div>
</template>

<script setup>
import { ref } from 'vue';
import { useRouter } from 'vue-router';
import ButtonPrime from 'primevue/button';
import DropdownPrime from 'primevue/dropdown';
import DjangoAuthGate from '@/components/estudyoo/DjangoAuthGate.vue';
import { ActividadesApi } from '@/services/EstudyooAdminApi';
import { isDjangoAdminAuthenticated, handleDjangoApiError } from '@/services/djangoAdminAuth';

const router = useRouter();
const djangoOk = ref(isDjangoAdminAuthenticated());
const colegios = ref([]);
const selColegioId = ref(null);
const cargandoColegios = ref(false);
const cargandoResumen = ref(false);
const cargado = ref(false);
const resumenError = ref('');
const statsVisible = ref(false);
const conteos = ref({});
const syncInfo = ref({});

const statGroups = [
  {
    id: 'personas',
    title: 'Colegio y alumnos',
    items: [
      { key: 'cursos', label: 'Cursos', icon: 'pi-users' },
      { key: 'estudiantes', label: 'Alumnos activos', icon: 'pi-user', hint: (c) => c.estudiantes_total > c.estudiantes ? `de ${c.estudiantes_total} total` : '' },
    ],
  },
  {
    id: 'catalogo',
    title: 'Catálogo (sync)',
    items: [
      { key: 'materias', label: 'Materias', icon: 'pi-book' },
      { key: 'ejes', label: 'Ejes / unidades', icon: 'pi-sitemap' },
      { key: 'capitulos', label: 'Capítulos', icon: 'pi-folder-open', hint: (c) => c.capitulos_sin_nivel ? `${c.capitulos_sin_nivel} sin nivel` : '' },
    ],
  },
  {
    id: 'actividades',
    title: 'Actividades y publicación',
    items: [
      { key: 'actividades', label: 'Actividades', icon: 'pi-palette', hint: (c) => c.actividades_activas ? `${c.actividades_activas} activas` : '' },
      { key: 'preguntas_actividad', label: 'Preguntas actividad', icon: 'pi-question-circle' },
      { key: 'asignaciones_activas', label: 'Asignadas a curso', icon: 'pi-share-alt', hint: (c) => c.asignaciones > c.asignaciones_activas ? `de ${c.asignaciones} total` : '' },
    ],
  },
  {
    id: 'material',
    title: 'Material e ítems',
    items: [
      { key: 'materiales_capitulo', label: 'Materiales capítulo', icon: 'pi-image' },
      { key: 'materiales_actividad', label: 'Materiales actividad', icon: 'pi-images' },
      { key: 'preguntas_juego', label: 'Preguntas juego', icon: 'pi-star' },
    ],
  },
];

function fmtNum(n) {
  if (n == null || n === '') return '0';
  return Number(n).toLocaleString('es-CL');
}

function fmtFecha(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('es-CL', { dateStyle: 'short', timeStyle: 'short' });
  } catch {
    return iso;
  }
}

async function cargarColegios() {
  cargandoColegios.value = true;
  try {
    const data = await ActividadesApi.listColegios({ page_size: 500 });
    const lista = Array.isArray(data) ? data : data.results || [];
    colegios.value = lista
      .filter((c) => c && c.id)
      .map((c) => ({
        id: c.id,
        label: c.nombre || c.Nombre || `Colegio ${String(c.id).slice(0, 8)}`,
      }))
      .sort((a, b) => a.label.localeCompare(b.label, 'es'));
  } catch (e) {
    colegios.value = [];
    resumenError.value = handleDjangoApiError(e)
      ? 'Sesión expirada. Vuelve a conectar arriba.'
      : (e.response?.data?.detail || e.message || 'No se pudieron cargar colegios.');
  } finally {
    cargandoColegios.value = false;
  }
}

async function cargarResumen() {
  if (!djangoOk.value) return;
  cargandoResumen.value = true;
  statsVisible.value = false;
  resumenError.value = '';
  try {
    const params = selColegioId.value ? { colegio_id: selColegioId.value } : {};
    const data = await ActividadesApi.getResumen(params);
    conteos.value = data.conteos || {};
    syncInfo.value = data.sync || {};
    cargado.value = true;
    requestAnimationFrame(() => {
      statsVisible.value = true;
    });
  } catch (e) {
    conteos.value = {};
    syncInfo.value = {};
    cargado.value = false;
    resumenError.value = handleDjangoApiError(e)
      ? 'Sesión expirada. Vuelve a conectar arriba.'
      : (e.response?.data?.detail || e.message || 'No se pudo cargar el resumen.');
  } finally {
    cargandoResumen.value = false;
  }
}

function onDjangoAuth(ok) {
  djangoOk.value = !!ok;
  if (ok) cargarColegios();
}
</script>

<style scoped>
.inv-report {
  max-width: 1080px;
  margin: 0 auto;
  padding: 0 0 2.5rem;
}
.inv-report__title {
  font-size: clamp(1.5rem, 4vw, 1.9rem);
  font-weight: 800;
  color: #0f172a;
  margin: 0 0 0.5rem;
}
.inv-report__lead {
  margin: 0;
  max-width: 42rem;
  font-size: 0.95rem;
  line-height: 1.55;
  color: #64748b;
}
.inv-report__gate {
  margin: 1rem 0 1.25rem;
}
.inv-report__panel {
  padding: 1.15rem 1.25rem;
  border-radius: 14px;
  border: 1px solid rgba(15, 23, 42, 0.08);
  background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
}
.inv-report__panel-head {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  margin-bottom: 1rem;
}
.inv-report__panel-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
}
.inv-report__colegio-dd {
  min-width: min(100%, 280px);
}
.inv-report__alert {
  font-size: 0.85rem;
  padding: 0.65rem 0.85rem;
  border-radius: 8px;
  margin-bottom: 1rem;
  background: #fff7ed;
  color: #c2410c;
  border: 1px solid #fed7aa;
}
.inv-report__empty {
  text-align: center;
  padding: 2.5rem 1rem;
  color: #64748b;
}
.inv-report__empty .pi {
  font-size: 2rem;
  margin-bottom: 0.75rem;
  color: #94a3b8;
}
.inv-report__stat-groups {
  display: flex;
  flex-direction: column;
  gap: 1.1rem;
}
.inv-report__stat-group-title {
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #94a3b8;
  margin: 0 0 0.5rem;
}
.inv-report__stats {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: 0.65rem;
}
.inv-report__stat {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  padding: 0.75rem 0.85rem;
  border-radius: 10px;
  background: #fff;
  border: 1px solid rgba(15, 23, 42, 0.06);
  opacity: 0;
  transform: translateY(6px);
  transition: opacity 0.35s ease, transform 0.35s ease;
}
.inv-report__stat--visible {
  opacity: 1;
  transform: translateY(0);
}
.inv-report__stat-icon {
  font-size: 0.9rem;
  color: #2563eb;
}
.inv-report__stat-value {
  font-size: 1.35rem;
  font-weight: 750;
  color: #0f172a;
}
.inv-report__stat-label {
  font-size: 0.78rem;
  color: #64748b;
}
.inv-report__stat-hint {
  font-size: 0.68rem;
  color: #94a3b8;
}
.inv-report__sync-banner {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
  margin-top: 1rem;
  padding: 0.65rem 0.85rem;
  border-radius: 8px;
  background: #ecfdf5;
  border: 1px solid #a7f3d0;
  font-size: 0.82rem;
  color: #065f46;
}
.inv-report__sync-hint {
  margin: 1rem 0 0;
  font-size: 0.82rem;
  color: #64748b;
}
@media (max-width: 576px) {
  .inv-report__stats {
    grid-template-columns: repeat(2, 1fr);
  }
}
</style>
